import React, { useCallback, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Upload, 
  X, 
  FileText, 
  Image, 
  File,
  AlertCircle
} from 'lucide-react';

interface FileItem {
  id: string;
  file: File;
  preview?: string;
  error?: string;
}

interface FileUploadProps {
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxSize?: number; // in MB
  allowedTypes?: string[];
  className?: string;
  key?: string; // Add key prop to force re-render
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesChange,
  maxFiles = 5,
  maxSize = 10,
  allowedTypes = [
    // Images
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    'image/bmp', 'image/tiff', 'image/heic', 'image/heif',
    // Audio
    'audio/mpeg', 'audio/mp4', 'audio/x-m4a', 'audio/wav', 'audio/x-wav',
    'audio/ogg', 'audio/flac', 'audio/aac',
    // Video
    'video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo',
    // Documents
    'application/pdf', 'text/plain', 'text/csv', 'application/json', 'application/xml',
    // Microsoft Office (extensions for better browser compatibility)
    '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
    // OpenDocument (LibreOffice)
    '.odt', '.ods', '.odp',
    // Archives
    'application/zip', 'application/x-zip-compressed',
    'application/vnd.rar', 'application/x-rar-compressed',
    'application/x-7z-compressed', 'application/gzip', 'application/x-tar',
  ],
  className = '',
  disabled = false
}) => {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const validateFile = useCallback((file: File): string | null => {
    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `File size must be less than ${maxSize}MB`;
    }

    // Check file type
    const isAllowed = allowedTypes.some(type => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase());
      }
      if (type.includes('*')) {
        const baseType = type.split('/')[0];
        return file.type.startsWith(baseType);
      }
      return file.type === type;
    });

    if (!isAllowed) {
      return 'File type not supported';
    }

    return null;
  }, [maxSize, allowedTypes]);

  const processFiles = useCallback((fileList: FileList) => {
    const newFiles: FileItem[] = [];
    
    Array.from(fileList).forEach((file, index) => {
      if (files.length + newFiles.length >= maxFiles) {
        return;
      }

      const error = validateFile(file);
      const fileItem: FileItem = {
        id: `${Date.now()}-${index}`,
        file,
        error: error || undefined
      };

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFiles(prev => 
            prev.map(f => 
              f.id === fileItem.id 
                ? { ...f, preview: e.target?.result as string }
                : f
            )
          );
        };
        reader.onerror = () => {
          console.warn('Failed to read image file for preview:', file.name);
        };
        reader.readAsDataURL(file);
      }

      newFiles.push(fileItem);
    });

    const updatedFiles = [...files, ...newFiles];
    setFiles(updatedFiles);
    
    // Notify parent of valid files only
    const validFiles = updatedFiles
      .filter(f => !f.error)
      .map(f => f.file);
    onFilesChange(validFiles);
  }, [files, maxFiles, onFilesChange, validateFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [processFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = ''; // Reset input
    }
  }, [processFiles]);

  const removeFile = (id: string) => {
    const updatedFiles = files.filter(f => f.id !== id);
    setFiles(updatedFiles);
    
    const validFiles = updatedFiles
      .filter(f => !f.error)
      .map(f => f.file);
    onFilesChange(validFiles);
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="h-4 w-4" />;
    }
    if (file.type === 'application/pdf' || file.type.includes('text')) {
      return <FileText className="h-4 w-4" />;
    }
    return <File className="h-4 w-4" />;
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <Card
        className={`
          relative border-2 border-dashed transition-colors
          ${disabled
            ? 'opacity-50 cursor-not-allowed border-gray-300 dark:border-gray-600'
            : `cursor-pointer ${dragActive
                ? 'border-primary bg-primary/5'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }`
          }
        `}
        onDragEnter={disabled ? undefined : handleDrag}
        onDragLeave={disabled ? undefined : handleDrag}
        onDragOver={disabled ? undefined : handleDrag}
        onDrop={disabled ? undefined : handleDrop}
      >
        <input
          type="file"
          multiple
          onChange={handleFileInput}
          accept={allowedTypes.join(',')}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          disabled={disabled || files.length >= maxFiles}
        />
        <div className="flex flex-col items-center justify-center p-8 text-center">
          <Upload className={`h-8 w-8 mb-2 ${disabled ? 'text-muted-foreground/50' : 'text-muted-foreground'}`} />
          <p className={`text-sm font-medium ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
            Drop files here or click to browse
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Max {maxFiles} files, up to {maxSize}MB each
          </p>
          <p className="text-xs text-muted-foreground">
            Supported: Images, Audio, Video, PDF, Office, Archives
          </p>
        </div>
      </Card>

      {/* File List */}
      {files.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Attached Files ({files.length})</h4>
          <div className="space-y-2">
            {files.map((fileItem) => (
              <Card key={fileItem.id} className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {fileItem.preview ? (
                      <img 
                        src={fileItem.preview} 
                        alt={fileItem.file.name}
                        className="h-10 w-10 object-cover rounded"
                      />
                    ) : (
                      <div className="h-10 w-10 bg-gray-100 dark:bg-gray-800 rounded flex items-center justify-center">
                        {getFileIcon(fileItem.file)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {fileItem.file.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-muted-foreground">
                          {(fileItem.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                        {fileItem.error && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {fileItem.error}
                          </Badge>
                        )}
                        {!fileItem.error && (
                          <Badge variant="secondary" className="text-xs">
                            Ready
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeFile(fileItem.id)}
                    className="flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};